import { useEffect, useState } from "react";

import { useSelector } from "common/utilities/react-redux";
import { upperCase } from "common/utilities/upperCase";
import { getIdToken } from "domains/authentication/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { globalNavRemoteEvent } from "domains/nav/constants/eventTypes";
import { executeHostCommand } from "domains/nav/helpers/executeHostCommand";
import { supportifyLanguage } from "domains/nav/helpers/supportifyLanguage";
import { useSubnavMenuItems } from "domains/nav/hooks/useSubnavMenuItems";
import { HostCommand } from "domains/nav/types/hostCommand";
import type { GlobalNavRemoteEvent } from "domains/nav/types/remoteEvent";
import { RemoteEvent } from "domains/nav/types/remoteEvent";
import { useTheme } from "domains/theme/useTheme";

export const useGlobalNavInit = (onCompletion: () => void) => {
  const idToken = useSelector(getIdToken);
  const siteLanguage = useSelector(getSiteLanguage);
  const { publicMenuItems, userMenuItems } = useSubnavMenuItems();
  const theme = useTheme();

  const language = supportifyLanguage(siteLanguage);
  const upperCaseTheme = upperCase(theme);

  const [isGlobalNavInitialised, setIsGlobalNavInitialised] = useState(false);

  useEffect(() => {
    const listener: EventListener = (event) => {
      if ((event as GlobalNavRemoteEvent).detail._type !== RemoteEvent.INIT_COMPLETED) return;
      setIsGlobalNavInitialised(true);

      executeHostCommand(HostCommand.SET_PLATFORM, {
        language,
        platform: "SI",
        theme: upperCaseTheme,
        token: idToken ?? null,
      });

      executeHostCommand(HostCommand.SUBNAV_SET_DATA, {
        public_menu_items: publicMenuItems,
        theme: upperCaseTheme,
        user_menu_items: userMenuItems,
      });

      onCompletion();
    };

    window.addEventListener(globalNavRemoteEvent, listener);
    return () => window.removeEventListener(globalNavRemoteEvent, listener);
  }, [idToken, language, publicMenuItems, userMenuItems, onCompletion, upperCaseTheme]);

  useEffect(() => {
    if (!isGlobalNavInitialised) return;

    executeHostCommand(HostCommand.SUBNAV_SET_DATA, {
      public_menu_items: publicMenuItems,
      theme: upperCaseTheme,
      user_menu_items: userMenuItems,
    });
  }, [isGlobalNavInitialised, publicMenuItems, upperCaseTheme, userMenuItems]);
};
